import type {Component} from 'grapesjs';
import type {CropData} from '@Panels/cropper-panel/cropper-panel.types';
import {getCorsReadUrl, loadImageAsync} from '@Utils/image.util';
import {EMAIL_MAKER_PROPS} from '@EmailMakerReact/email-maker-react.constants';
import {getStringProp} from '@EmailMakerReact/lib/email-maker-component-query-library';
import {getCroppedMediaUrl} from '@EmailMakerReact/lib/email-maker-cropped-media-url-library';
import {getStoredCropData} from '@EmailMakerReact/lib/email-maker-crop-data-library';
import {logEmailMakerError} from '@EmailMakerReact/lib/email-maker-error-library';

const SRC_ATTRIBUTE = 'src';
const PREVIEW_MIME_TYPE = 'image/png';
const CANVAS_CONTEXT_TYPE = '2d';

interface CroppedImageSource {
  uncroppedSrc: string;
  cropData: CropData;
  croppedSrc: string;
}

const previewObjectUrls = new WeakMap<Component, string>();

export async function showCroppedImagePreview(component: Component): Promise<void> {
  const croppedImageSource = getCroppedImageSource(component);
  if (!croppedImageSource) {
    return;
  }

  let previewUrl: string;
  try {
    const uncroppedImage = await loadImageAsync(getCorsReadUrl(croppedImageSource.uncroppedSrc));
    previewUrl = URL.createObjectURL(await drawCroppedImage(uncroppedImage, croppedImageSource.cropData));
  } catch (error) {
    logEmailMakerError('showCroppedImagePreview: failed to draw the cropped image preview', error);
    return;
  }

  const element = component.getEl();
  if (!element || !isPreviewStillWanted(component, croppedImageSource.croppedSrc, element)) {
    URL.revokeObjectURL(previewUrl);
    return;
  }

  revokeCroppedImagePreview(component);
  previewObjectUrls.set(component, previewUrl);
  element.setAttribute(SRC_ATTRIBUTE, previewUrl);
}

export function showCroppedImagePreviewForFailedSrc(component: Component, element: HTMLElement): void {
  const croppedImageSource = getCroppedImageSource(component);
  if (!croppedImageSource || element.getAttribute(SRC_ATTRIBUTE) !== getStringProp(component, SRC_ATTRIBUTE)) {
    return;
  }
  void showCroppedImagePreview(component);
}

export function revokeCroppedImagePreview(component: Component): void {
  const previewUrl = previewObjectUrls.get(component);
  if (!previewUrl) {
    return;
  }
  previewObjectUrls.delete(component);
  URL.revokeObjectURL(previewUrl);
}

function getCroppedImageSource(component: Component): CroppedImageSource | null {
  const cropData = getStoredCropData(component);
  const uncroppedSrc = getStringProp(component, EMAIL_MAKER_PROPS.UNCROPPED_SRC);
  if (!cropData || !uncroppedSrc) {
    return null;
  }
  const croppedSrc = getCroppedMediaUrl(uncroppedSrc, cropData);
  if (!croppedSrc) {
    return null;
  }
  return {uncroppedSrc, cropData, croppedSrc};
}

function isPreviewStillWanted(component: Component, croppedSrc: string, element: HTMLElement): boolean {
  const modelSrc = getStringProp(component, SRC_ATTRIBUTE);
  if (modelSrc !== croppedSrc) {
    return false;
  }
  const shownSrc = element.getAttribute(SRC_ATTRIBUTE);
  return shownSrc === modelSrc || shownSrc === previewObjectUrls.get(component);
}

async function drawCroppedImage(image: HTMLImageElement, cropData: CropData): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = cropData.width;
  canvas.height = cropData.height;
  const context = canvas.getContext(CANVAS_CONTEXT_TYPE);
  if (!context) {
    throw new Error('Canvas 2D context is unavailable');
  }
  context.drawImage(image, cropData.x, cropData.y, cropData.width, cropData.height, 0, 0, cropData.width, cropData.height);
  return getCanvasBlob(canvas);
}

function getCanvasBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise<Blob>((resolve, reject): void => {
    canvas.toBlob((blob): void => {
      if (!blob) {
        reject(new Error('The cropped image preview could not be encoded'));
        return;
      }
      resolve(blob);
    }, PREVIEW_MIME_TYPE);
  });
}
